/**
 * Main game engine class that orchestrates game logic
 */
import { createGrid, occupyCells, freeCells, validatePlacement, expandGrid, shrinkGrid } from '../types/grid';
import { getPieceCells, PIECE_DEFINITIONS, getShapeForPiece } from '../types/tetromino';
import { DEFAULT_CONFIG, TICK_DELTA } from '../types/config';
import { parseTiling, stringifyTiling } from '../types/tiling';
export class GameEngine {
    gameState;
    pieceIdCounter = 0;
    config;
    accumulatedTime = 0;
    gridPieceMap = new Map(); // Spatial index: "x,y" -> piece
    // Caches for tick optimization
    hasStoragePieces = false;
    generatorPieces = [];
    enhancementMultiplierCache = new Map(); // pieceId -> multiplier
    boardStateChanged = true; // True after pieces added/removed/modified
    constructor(initialWidth = 4, initialHeight = 4, config = DEFAULT_CONFIG) {
        this.config = config;
        const mapSizeCfg = this.lookupMapSizeConfig(initialWidth, initialHeight, 1);
        this.gameState = {
            grid: createGrid(initialWidth, initialHeight),
            pieces: [],
            selectedPieceType: null,
            selectedRotation: 0,
            selectedMirrored: false,
            currency: config.initialCurrency,
            maxCurrency: config.baseMaxCurrency,
            tax: mapSizeCfg.tax,
            rent: mapSizeCfg.rent,
            prestigeLevel: 1,
        };
    }
    /**
     * Get current game state (immutable copy)
     */
    getState() {
        return {
            grid: {
                ...this.gameState.grid,
                cells: this.gameState.grid.cells.map(row => [...row]),
            },
            pieces: [...this.gameState.pieces],
            selectedPieceType: this.gameState.selectedPieceType,
            selectedRotation: this.gameState.selectedRotation,
            selectedMirrored: this.gameState.selectedMirrored,
            currency: this.gameState.currency,
            maxCurrency: this.gameState.maxCurrency,
            tax: this.gameState.tax,
            rent: this.gameState.rent,
            prestigeLevel: this.gameState.prestigeLevel,
        };
    }
    /**
     * Reset game state to initial values (for reusing engine instance between evaluations)
     * Clears all game data and returns to clean initial state
     */
    resetGameState() {
        this.accumulatedTime = 0;
        this.pieceIdCounter = 0;
        this.clearPieceIndex();
        const mapSizeCfg = this.lookupMapSizeConfig(this.gameState.grid.width, this.gameState.grid.height);
        this.gameState = {
            grid: createGrid(this.gameState.grid.width, this.gameState.grid.height),
            pieces: [],
            selectedPieceType: null,
            selectedRotation: 0,
            selectedMirrored: false,
            currency: this.config.initialCurrency,
            maxCurrency: this.config.baseMaxCurrency,
            tax: mapSizeCfg.tax,
            rent: mapSizeCfg.rent,
            prestigeLevel: this.gameState.prestigeLevel,
        };
        this.updateCaches();
    }
    /**
     * Select a piece type for placement, or null to deselect
     */
    selectPieceType(type) {
        this.gameState.selectedPieceType = type;
        this.gameState.selectedRotation = 0; // Reset rotation when changing piece
        this.gameState.selectedMirrored = false; // Reset mirroring when changing piece
    }
    /**
     * Rotate selected piece
     */
    rotatePiece(clockwise) {
        if (!this.gameState.selectedPieceType)
            return;
        const rotations = PIECE_DEFINITIONS[this.gameState.selectedPieceType];
        const maxRotations = rotations.length;
        if (clockwise) {
            this.gameState.selectedRotation = (this.gameState.selectedRotation + 1) % maxRotations;
        }
        else {
            this.gameState.selectedRotation = (this.gameState.selectedRotation - 1 + maxRotations) % maxRotations;
        }
    }
    /**
     * Mirror selected piece
     */
    mirrorPiece() {
        this.gameState.selectedMirrored = !this.gameState.selectedMirrored;
    }
    /**
     * Place a piece at grid coordinates
     */
    placePiece(cursorX, cursorY) {
        if (!this.gameState.selectedPieceType) {
            return false;
        }
        const newPiece = {
            type: this.gameState.selectedPieceType,
            rotations: PIECE_DEFINITIONS[this.gameState.selectedPieceType],
            currentRotation: this.gameState.selectedRotation,
            mirrored: this.gameState.selectedMirrored,
        };
        const shape = getShapeForPiece(newPiece);
        // Calculate gridX and gridY so that cells[0] stays at cursor position
        const anchor = shape.cells[0];
        const gridX = cursorX - anchor.x;
        const gridY = cursorY - anchor.y;
        const tileConfig = this.config.tileConfigs[newPiece.type];
        const placedPiece = {
            ...newPiece,
            id: this.generatePieceId(),
            gridX,
            gridY,
            state: 'active',
            stateTimer: tileConfig.activeDuration,
            level: 1,
        };
        const cells = getPieceCells(placedPiece);
        const error = validatePlacement(cells, this.gameState.grid);
        if (error) {
            return false;
        }
        // Check if player has enough currency
        const cost = this.config.tileConfigs[newPiece.type].cost;
        if (this.gameState.currency < cost) {
            return false;
        }
        this.gameState.grid = occupyCells(cells, this.gameState.grid);
        this.gameState.pieces.push(placedPiece);
        this.addPieceToIndex(placedPiece);
        this.gameState.currency -= cost;
        // Update caches and recalculate after placing piece
        this.updateCaches();
        // Recalculate all synchronization networks globally
        this.recalculateAllSynchronizationNetworks();
        // Clear caches again after network recalculation to ensure fresh enhancement calculations
        this.enhancementMultiplierCache.clear();
        this.gameState.maxCurrency = this.calculateMaxCurrency();
        return true;
    }
    /**
     * Remove a piece from the board and refund 80% of cost
     */
    removePiece(pieceId) {
        const pieceIndex = this.gameState.pieces.findIndex((p) => p.id === pieceId);
        if (pieceIndex === -1)
            return false;
        const piece = this.gameState.pieces[pieceIndex];
        const cells = getPieceCells(piece);
        const originalCost = this.config.tileConfigs[piece.type].cost;
        const refund = Math.floor(originalCost * this.config.sellRefundRatio);
        this.gameState.grid = freeCells(cells, this.gameState.grid);
        this.removePieceFromIndex(piece);
        this.gameState.pieces.splice(pieceIndex, 1);
        this.gameState.currency += refund;
        // Update caches and recalculate after removing piece
        this.updateCaches();
        // Recalculate all synchronization networks globally
        this.recalculateAllSynchronizationNetworks();
        // Clear caches again after network recalculation to ensure fresh enhancement calculations
        this.enhancementMultiplierCache.clear();
        this.gameState.maxCurrency = this.calculateMaxCurrency();
        // Always cap currency to max (in case refund pushes over the limit)
        this.capCurrencyToMax();
        return true;
    }
    /**
     * Clear the board
     */
    clearBoard() {
        this.gameState.grid = createGrid(this.gameState.grid.width, this.gameState.grid.height);
        this.gameState.pieces = [];
        this.clearPieceIndex();
        this.gameState.maxCurrency = this.config.baseMaxCurrency;
        this.capCurrencyToMax();
        this.updateCaches();
    }
    /**
     * Expand grid for currency cost
     */
    expandGrid(dimension) {
        const currentGrid = this.gameState.grid;
        const maxSize = 8;
        let newWidth = currentGrid.width;
        let newHeight = currentGrid.height;
        if (dimension === 'width') {
            newWidth = Math.min(currentGrid.width + 2, maxSize);
        }
        else {
            newHeight = Math.min(currentGrid.height + 2, maxSize);
        }
        if (newWidth === currentGrid.width && newHeight === currentGrid.height) {
            return false; // Already at max size
        }
        // Check if player has enough currency
        const mapSizeCfgNew = this.lookupMapSizeConfig(newWidth, newHeight);
        const cost = mapSizeCfgNew.expansionCost;
        if (this.gameState.currency < cost) {
            return false; // Insufficient currency
        }
        // Deduct currency and expand grid
        this.gameState.currency -= cost;
        this.gameState.grid = expandGrid(this.gameState.grid, dimension);
        // Update tax/rent for new map size
        this.gameState.tax = mapSizeCfgNew.tax;
        this.gameState.rent = mapSizeCfgNew.rent;
        return true;
    }
    /**
     * Sell (shrink) the last expansion on a given dimension, refunding tiles and auto-removing pieces
     */
    shrinkGrid(dimension) {
        const minSize = 4;
        const currentGrid = this.gameState.grid;
        if (dimension === 'width' && currentGrid.width <= minSize)
            return false;
        if (dimension === 'height' && currentGrid.height <= minSize)
            return false;
        const newWidth = dimension === 'width' ? currentGrid.width - 2 : currentGrid.width;
        const newHeight = dimension === 'height' ? currentGrid.height - 2 : currentGrid.height;
        // Find all pieces that have at least one cell in the area being removed
        const piecesToRemove = this.gameState.pieces.filter((piece) => {
            const cells = getPieceCells(piece);
            return cells.some((cell) => dimension === 'width' ? cell.x >= newWidth : cell.y >= newHeight);
        });
        // Remove each affected piece and refund its cost
        for (const piece of piecesToRemove) {
            const originalCost = this.config.tileConfigs[piece.type].cost;
            const refund = Math.floor(originalCost * this.config.sellRefundRatio);
            const cells = getPieceCells(piece);
            this.gameState.grid = freeCells(cells, this.gameState.grid);
            this.removePieceFromIndex(piece);
            const idx = this.gameState.pieces.indexOf(piece);
            this.gameState.pieces.splice(idx, 1);
            this.gameState.currency += refund;
        }
        // Refund the expansion cost for this size
        const currentSizeCfg = this.lookupMapSizeConfig(currentGrid.width, currentGrid.height);
        const expansionRefund = Math.floor(currentSizeCfg.expansionCost * this.config.sellRefundRatio);
        this.gameState.currency += expansionRefund;
        // Shrink the grid
        this.gameState.grid = shrinkGrid(this.gameState.grid, dimension);
        // Update caches and recalculate networks
        this.updateCaches();
        this.recalculateAllSynchronizationNetworks();
        this.enhancementMultiplierCache.clear();
        this.gameState.maxCurrency = this.calculateMaxCurrency();
        // Update tax/rent for new map size
        const mapSizeCfg = this.lookupMapSizeConfig(this.gameState.grid.width, this.gameState.grid.height);
        this.gameState.tax = mapSizeCfg.tax;
        this.gameState.rent = mapSizeCfg.rent;
        this.capCurrencyToMax();
        return true;
    }
    /**
     * Calculate the actual effect value for a piece given the current board state
     * Returns the generation rate, storage bonus, or other effect value after applying enhancements
     */
    calculatePieceEffectValue(piece) {
        const config = this.config.tileConfigs[piece.type];
        // For O pieces (generators), calculate actual generation rate
        if (config.generationRate > 0) {
            const enhancementMultiplier = this.calculateEnhancementMultiplier(piece);
            return this.getPieceLeveledEffect(piece, config.generationRate) * (1 + enhancementMultiplier);
        }
        // For S pieces (storage), calculate actual bonus
        if (config.storageBonus && config.storageBonus > 0) {
            const enhancementMultiplier = this.calculateEnhancementMultiplier(piece);
            return this.getPieceLeveledEffect(piece, config.storageBonus) * (1 + enhancementMultiplier);
        }
        // For L pieces (cooldown reduction), calculate actual reduction
        if (config.cooldownReduction && config.cooldownReduction > 0) {
            const enhancementMultiplier = this.calculateEnhancementMultiplier(piece);
            return this.getPieceLeveledEffect(piece, config.cooldownReduction) * (1 + enhancementMultiplier);
        }
        // For other pieces, return 0
        return 0;
    }
    /**
     * Get the enhancement multiplier for a piece (public accessor)
     */
    getPieceEnhancementMultiplier(piece) {
        return this.calculateEnhancementMultiplier(piece);
    }
    /**
     * Update game state with fixed timestep
     * Accumulates deltaTime and processes ticks at TICK_DELTA intervals
     */
    tick(deltaTime) {
        this.accumulatedTime += deltaTime;
        // Process all accumulated ticks
        while (this.accumulatedTime >= TICK_DELTA) {
            this.accumulatedTime -= TICK_DELTA;
            let enhancerStateChanged = false;
            // Process all pieces for state transitions and currency generation
            for (const piece of this.gameState.pieces) {
                const config = this.config.tileConfigs[piece.type];
                // Only process pieces with configured durations
                if (config.activeDuration === 0 && config.cooldownDuration === 0) {
                    continue;
                }
                // Generate currency if in active state - only for generator pieces
                if (piece.state === 'active' && config.generationRate > 0) {
                    // Use cached enhancement multiplier if board is stable, otherwise recalculate
                    let enhancementMultiplier;
                    if (this.boardStateChanged) {
                        enhancementMultiplier = this.calculateEnhancementMultiplier(piece);
                        this.enhancementMultiplierCache.set(piece.id, enhancementMultiplier);
                    }
                    else {
                        enhancementMultiplier = this.enhancementMultiplierCache.get(piece.id) ?? 0;
                    }
                    const leveledRate = this.getPieceLeveledEffect(piece, config.generationRate);
                    const effectiveRate = Math.max(0, leveledRate * (1 + enhancementMultiplier) - this.gameState.tax);
                    this.gameState.currency += effectiveRate * TICK_DELTA;
                }
                // For pieces with 0 cooldown, always stay active
                const cooldownDuration = piece.cooldownDuration ?? config.cooldownDuration;
                if (cooldownDuration === 0) {
                    piece.state = 'active';
                    piece.stateTimer = 0;
                    continue;
                }
                // Update state timer
                piece.stateTimer -= TICK_DELTA;
                // Transition states when timer expires
                if (piece.stateTimer <= 0) {
                    const oldState = piece.state;
                    if (piece.state === 'active') {
                        piece.state = 'cooldown';
                        piece.stateTimer = cooldownDuration;
                    }
                    else {
                        piece.state = 'active';
                        piece.stateTimer = config.activeDuration;
                    }
                    // Track if an enhancer (I piece) changed state
                    if (piece.type === 'I' && oldState !== piece.state) {
                        enhancerStateChanged = true;
                    }
                }
            }
            // If an enhancer changed state, invalidate enhancement multiplier cache so all pieces recalculate on next tick
            if (enhancerStateChanged) {
                this.enhancementMultiplierCache.clear();
                this.boardStateChanged = true;
            }
            else {
                // Mark board as stable only if no enhancer state change occurred
                this.boardStateChanged = false;
            }
            // Recalculate max currency only if there are storage pieces and state changed
            if (this.hasStoragePieces && (enhancerStateChanged || this.boardStateChanged)) {
                this.gameState.maxCurrency = this.calculateMaxCurrency();
            }
            // Apply rent drain (floors at 0)
            if (this.gameState.rent > 0) {
                this.gameState.currency = Math.max(0, this.gameState.currency - this.gameState.rent * TICK_DELTA);
            }
            // Cap currency to maximum
            this.gameState.currency = Math.min(this.gameState.currency, this.gameState.maxCurrency);
        }
    }
    /**
     * Reset the game engine
     */
    reset() {
        this.clearPieceIndex();
        const mapSizeCfg = this.lookupMapSizeConfig(4, 4, 1);
        this.gameState = {
            grid: createGrid(4, 4),
            pieces: [],
            selectedPieceType: null,
            selectedRotation: 0,
            selectedMirrored: false,
            currency: this.config.initialCurrency,
            maxCurrency: this.config.baseMaxCurrency,
            tax: mapSizeCfg.tax,
            rent: mapSizeCfg.rent,
            prestigeLevel: 1,
        };
        this.pieceIdCounter = 0;
        this.accumulatedTime = 0;
        this.updateCaches();
    }
    /**
     * Prestige: sell all expansions and pieces, deduct prestige cost, increment prestige level.
     * Only available when the grid is at 8x8.
     */
    prestige() {
        const { width, height } = this.gameState.grid;
        if (width < 8 || height < 8)
            return false;
        const currentCfg = this.lookupMapSizeConfig(width, height);
        const prestigeCost = currentCfg.prestigeCost ?? 0;
        // Sell all pieces with refunds
        const piecesToRemove = [...this.gameState.pieces];
        for (const piece of piecesToRemove) {
            const originalCost = this.config.tileConfigs[piece.type].cost;
            const refund = Math.floor(originalCost * this.config.sellRefundRatio);
            const cells = getPieceCells(piece);
            this.gameState.grid = freeCells(cells, this.gameState.grid);
            this.removePieceFromIndex(piece);
            this.gameState.currency += refund;
        }
        this.gameState.pieces = [];
        // Shrink grid back to 4x4 (collect expansion refunds at each step)
        while (this.gameState.grid.height > 4) {
            this.shrinkGrid('height');
        }
        while (this.gameState.grid.width > 4) {
            this.shrinkGrid('width');
        }
        // Deduct prestige cost
        this.gameState.currency = Math.max(0, this.gameState.currency - prestigeCost);
        // Increment prestige level
        this.gameState.prestigeLevel++;
        // Update tax/rent for 4x4 at new prestige level
        const mapSizeCfg = this.lookupMapSizeConfig(4, 4);
        this.gameState.tax = mapSizeCfg.tax;
        this.gameState.rent = mapSizeCfg.rent;
        this.updateCaches();
        this.enhancementMultiplierCache.clear();
        this.gameState.maxCurrency = this.calculateMaxCurrency();
        this.capCurrencyToMax();
        return true;
    }
    /**
     * Upgrade a placed piece to the next level, up to the current prestige level.
     * Deducts the upgrade cost and recalculates all effects.
     */
    upgradePiece(pieceId) {
        const piece = this.gameState.pieces.find(p => p.id === pieceId);
        if (!piece)
            return false;
        const maxLevel = this.gameState.prestigeLevel;
        if (piece.level >= maxLevel)
            return false;
        const config = this.config.tileConfigs[piece.type];
        const upgradeCosts = config.upgradeCosts ?? [];
        const upgradeCost = upgradeCosts[piece.level - 1]; // index 0 = cost for level 1→2
        if (upgradeCost === undefined)
            return false;
        if (this.gameState.currency < upgradeCost)
            return false;
        this.gameState.currency -= upgradeCost;
        piece.level++;
        this.updateCaches();
        this.recalculateAllSynchronizationNetworks();
        this.enhancementMultiplierCache.clear();
        this.gameState.maxCurrency = this.calculateMaxCurrency();
        this.capCurrencyToMax();
        return true;
    }
    /**
     * Load a tiling configuration from a TilingString
     * Format: "O(0,0,0,0),I(2,0,1,1),L(4,1,2,0)"
     * Directly places all pieces without validation to allow importing optimized tilings
     * Automatically expands the grid if needed, then resets currency to initial amount
     */
    loadTiling(tilingStr) {
        const tilingPieces = parseTiling(tilingStr);
        // Calculate required grid size
        let maxX = 0;
        let maxY = 0;
        for (const tilingPiece of tilingPieces) {
            const shape = getShapeForPiece({
                type: tilingPiece.type,
                rotations: PIECE_DEFINITIONS[tilingPiece.type],
                currentRotation: tilingPiece.rotation,
                mirrored: tilingPiece.mirrored,
            });
            const cellsX = tilingPiece.gridX + shape.width;
            const cellsY = tilingPiece.gridY + shape.height;
            maxX = Math.max(maxX, cellsX);
            maxY = Math.max(maxY, cellsY);
        }
        // Clear current pieces first
        this.gameState.pieces = [];
        this.pieceIdCounter = 0;
        this.clearPieceIndex();
        this.gameState.grid = createGrid(this.gameState.grid.width, this.gameState.grid.height);
        // Expand grid to required size if needed
        while (this.gameState.grid.width < maxX || this.gameState.grid.height < maxY) {
            const needsWidthExpand = this.gameState.grid.width < maxX;
            const needsHeightExpand = this.gameState.grid.height < maxY;
            // Expand grid
            const dimension = (needsWidthExpand && needsHeightExpand) ? 'both' : (needsWidthExpand ? 'width' : 'height');
            this.gameState.grid = expandGrid(this.gameState.grid, dimension);
        }
        // Directly add all pieces without going through placePiece validation
        for (const tilingPiece of tilingPieces) {
            const config = this.config.tileConfigs[tilingPiece.type];
            // Create placed piece
            const placedPiece = {
                id: this.generatePieceId(),
                type: tilingPiece.type,
                gridX: tilingPiece.gridX,
                gridY: tilingPiece.gridY,
                currentRotation: tilingPiece.rotation,
                mirrored: tilingPiece.mirrored,
                rotations: PIECE_DEFINITIONS[tilingPiece.type],
                state: 'active',
                stateTimer: config.activeDuration,
                level: 1,
            };
            // Mark cells as occupied in grid
            const cells = getPieceCells(placedPiece);
            this.gameState.grid = occupyCells(cells, this.gameState.grid);
            // Add piece to game state and spatial index
            this.gameState.pieces.push(placedPiece);
            this.addPieceToIndex(placedPiece);
        }
        // Update caches and recalculate after loading
        this.updateCaches();
        this.recalculateAllSynchronizationNetworks();
        // Clear caches again after network recalculation to ensure fresh enhancement calculations
        this.enhancementMultiplierCache.clear();
        this.gameState.maxCurrency = this.calculateMaxCurrency();
        // Reset currency to initial amount after buying necessary map upgrades
        this.gameState.currency = this.config.initialCurrency;
        return true;
    }
    /**
     * Export current board configuration as a TilingString
     * Returns format: "O(0,0,0,0),I(2,0,1,1),L(4,1,2,0)"
     */
    exportTiling() {
        const pieces = this.gameState.pieces.map(p => ({
            type: p.type,
            gridX: p.gridX,
            gridY: p.gridY,
            rotation: p.currentRotation,
            mirrored: p.mirrored,
        }));
        return stringifyTiling(pieces);
    }
    /**
     * Add a piece's cells to the spatial index
     */
    addPieceToIndex(piece) {
        const cells = getPieceCells(piece);
        for (const cell of cells) {
            this.gridPieceMap.set(`${cell.x},${cell.y}`, piece);
        }
    }
    /**
     * Remove a piece's cells from the spatial index
     */
    removePieceFromIndex(piece) {
        const cells = getPieceCells(piece);
        for (const cell of cells) {
            this.gridPieceMap.delete(`${cell.x},${cell.y}`);
        }
    }
    /**
     * Clear the spatial index
     */
    clearPieceIndex() {
        this.gridPieceMap.clear();
    }
    /**
     * Update cached values after board changes
     */
    updateCaches() {
        this.boardStateChanged = true;
        this.enhancementMultiplierCache.clear();
        // Update hasStoragePieces flag
        this.hasStoragePieces = this.gameState.pieces.some(p => this.config.tileConfigs[p.type].storageBonus);
        // Update generatorPieces list
        this.generatorPieces = this.gameState.pieces.filter(p => this.config.tileConfigs[p.type].generationRate > 0);
    }
    /**
     * Find a piece at a specific grid cell (O(1) using spatial index)
     */
    getPieceAtCell(x, y) {
        return this.gridPieceMap.get(`${x},${y}`) ?? null;
    }
    /**
     * Calculate the total enhancement multiplier for a piece based on adjacent active enhancers.
     * Checks direct I neighbors, and for each adjacent T piece checks its network for active I pieces.
     */
    calculateEnhancementMultiplier(piece) {
        let totalMultiplier = 0;
        const cells = getPieceCells(piece);
        const neighborOffsets = [
            { dx: 0, dy: -1 }, // Top
            { dx: -1, dy: 0 }, // Left
            { dx: 1, dy: 0 }, // Right
            { dx: 0, dy: 1 }, // Bottom
        ];
        const countedEnhancers = new Set();
        for (const cell of cells) {
            for (const offset of neighborOffsets) {
                const neighborX = cell.x + offset.dx;
                const neighborY = cell.y + offset.dy;
                const neighborPiece = this.getPieceAtCell(neighborX, neighborY);
                if (!neighborPiece)
                    continue;
                if (neighborPiece.type === 'I' && neighborPiece.state === 'active' && !countedEnhancers.has(neighborPiece.id)) {
                    const config = this.config.tileConfigs[neighborPiece.type];
                    if (config.enhancerMultiplier && config.enhancerMultiplier > 0) {
                        totalMultiplier += this.getPieceLeveledEffect(neighborPiece, config.enhancerMultiplier);
                        countedEnhancers.add(neighborPiece.id);
                    }
                }
                else if (neighborPiece.type === 'T') {
                    totalMultiplier += this.calculateTNetworkEnhancement(neighborPiece, countedEnhancers);
                }
            }
        }
        return totalMultiplier;
    }
    /**
     * Returns the total enhancement multiplier conducted through a T piece's network.
     * For each active I piece in the network, finds the best (max) conductingFactor among
     * T pieces adjacent to that I piece, then multiplies by the I's enhancerMultiplier.
     * Already-counted enhancers are skipped to avoid double-counting when multiple T pieces
     * are adjacent to the receiving piece.
     */
    calculateTNetworkEnhancement(tPiece, countedEnhancers) {
        if (!tPiece.tNetworkId)
            return 0;
        let totalMultiplier = 0;
        for (const networkPiece of this.gameState.pieces) {
            if (networkPiece.type === 'I' &&
                networkPiece.state === 'active' &&
                networkPiece.tNetworkId === tPiece.tNetworkId &&
                !countedEnhancers.has(networkPiece.id)) {
                const config = this.config.tileConfigs[networkPiece.type];
                if (config.enhancerMultiplier && config.enhancerMultiplier > 0) {
                    const adjacentTs = this.findAdjacentTsInNetwork(networkPiece, tPiece.tNetworkId);
                    if (adjacentTs.length === 0)
                        continue;
                    const maxFactor = Math.max(...adjacentTs.map(t => t.conductingFactor ?? 0.5));
                    totalMultiplier += maxFactor * this.getPieceLeveledEffect(networkPiece, config.enhancerMultiplier);
                    countedEnhancers.add(networkPiece.id);
                }
            }
        }
        return totalMultiplier;
    }
    /**
     * Calculate the total cooldown reduction received by a piece from adjacent L pieces and T-conducted L effects.
     */
    calculateReceivedCooldownReduction(piece) {
        let totalReduction = 0;
        const cells = getPieceCells(piece);
        const neighborOffsets = [
            { dx: 0, dy: -1 }, // Top
            { dx: -1, dy: 0 }, // Left
            { dx: 1, dy: 0 }, // Right
            { dx: 0, dy: 1 }, // Bottom
        ];
        const countedReducers = new Set();
        for (const cell of cells) {
            for (const offset of neighborOffsets) {
                const neighborX = cell.x + offset.dx;
                const neighborY = cell.y + offset.dy;
                const neighborPiece = this.getPieceAtCell(neighborX, neighborY);
                if (!neighborPiece)
                    continue;
                if (neighborPiece.type === 'L' && !countedReducers.has(neighborPiece.id)) {
                    const config = this.config.tileConfigs[neighborPiece.type];
                    if (config.cooldownReduction && config.cooldownReduction > 0) {
                        const enhancementMultiplier = this.calculateEnhancementMultiplier(neighborPiece);
                        totalReduction += this.getPieceLeveledEffect(neighborPiece, config.cooldownReduction) * (1 + enhancementMultiplier);
                        countedReducers.add(neighborPiece.id);
                    }
                }
                else if (neighborPiece.type === 'T') {
                    totalReduction += this.calculateTNetworkCooldownReduction(neighborPiece, countedReducers);
                }
            }
        }
        return totalReduction;
    }
    /**
     * Returns the total cooldown reduction conducted through a T piece's network.
     * For each L piece in the network, finds the best (max) conductingFactor among
     * T pieces adjacent to that L piece, then applies that fraction of the L's reduction.
     * Already-counted reducers are skipped to avoid double-counting when multiple T pieces
     * are adjacent to the receiving piece.
     */
    calculateTNetworkCooldownReduction(tPiece, countedReducers) {
        if (!tPiece.tNetworkId)
            return 0;
        let totalReduction = 0;
        for (const networkPiece of this.gameState.pieces) {
            if (networkPiece.type === 'L' &&
                networkPiece.tNetworkId === tPiece.tNetworkId &&
                !countedReducers.has(networkPiece.id)) {
                const config = this.config.tileConfigs[networkPiece.type];
                if (config.cooldownReduction && config.cooldownReduction > 0) {
                    const adjacentTs = this.findAdjacentTsInNetwork(networkPiece, tPiece.tNetworkId);
                    if (adjacentTs.length === 0)
                        continue;
                    const maxFactor = Math.max(...adjacentTs.map(t => t.conductingFactor ?? 0.5));
                    const enhancementMultiplier = this.calculateEnhancementMultiplier(networkPiece);
                    totalReduction += maxFactor * this.getPieceLeveledEffect(networkPiece, config.cooldownReduction) * (1 + enhancementMultiplier);
                    countedReducers.add(networkPiece.id);
                }
            }
        }
        return totalReduction;
    }
    /**
     * Find all T pieces adjacent to a given piece that belong to the specified network.
     */
    findAdjacentTsInNetwork(piece, networkId) {
        const result = [];
        const seen = new Set();
        const cells = getPieceCells(piece);
        const neighborOffsets = [
            { dx: 0, dy: -1 },
            { dx: -1, dy: 0 },
            { dx: 1, dy: 0 },
            { dx: 0, dy: 1 },
        ];
        for (const cell of cells) {
            for (const offset of neighborOffsets) {
                const neighbor = this.getPieceAtCell(cell.x + offset.dx, cell.y + offset.dy);
                if (neighbor && neighbor.type === 'T' && neighbor.tNetworkId === networkId && !seen.has(neighbor.id)) {
                    result.push(neighbor);
                    seen.add(neighbor.id);
                }
            }
        }
        return result;
    }
    /**
     * Returns the effective value of a piece's base effect scaled by its level.
     * Formula: baseValue + (level - 1) * upgradeEffectPerLevel
     */
    getPieceLeveledEffect(piece, baseValue) {
        const upgradePerLevel = this.config.tileConfigs[piece.type].upgradeEffectPerLevel ?? 0;
        return baseValue + (piece.level - 1) * upgradePerLevel;
    }
    /**
     * Look up tax/rent config for a given grid size and prestige level, defaulting to zero if not configured
     */
    lookupMapSizeConfig(width, height, prestigeLevel) {
        const level = prestigeLevel ?? this.gameState?.prestigeLevel ?? 1;
        return this.config.mapSizeConfigs[`${width}x${height}x${level}`] ?? { tax: 0, rent: 0, expansionCost: 0 };
    }
    /**
     * Generate a unique piece ID
     */
    generatePieceId() {
        return `piece_${this.pieceIdCounter++}`;
    }
    /**
     * Find all T pieces transitively connected to a given T piece (including the piece itself)
     */
    findConnectedTPieces(tPieceId) {
        const visited = new Set();
        const queue = [tPieceId];
        while (queue.length > 0) {
            const currentId = queue.shift();
            if (visited.has(currentId))
                continue;
            visited.add(currentId);
            const currentPiece = this.gameState.pieces.find(p => p.id === currentId);
            if (!currentPiece || currentPiece.type !== 'T')
                continue;
            const cells = getPieceCells(currentPiece);
            const neighborOffsets = [
                { dx: 0, dy: -1 }, // Top
                { dx: -1, dy: 0 }, // Left
                { dx: 1, dy: 0 }, // Right
                { dx: 0, dy: 1 }, // Bottom
            ];
            // Find all T pieces adjacent to this piece
            for (const cell of cells) {
                for (const offset of neighborOffsets) {
                    const neighborX = cell.x + offset.dx;
                    const neighborY = cell.y + offset.dy;
                    const neighbor = this.getPieceAtCell(neighborX, neighborY);
                    if (neighbor && neighbor.type === 'T' && !visited.has(neighbor.id)) {
                        queue.push(neighbor.id);
                    }
                }
            }
        }
        return Array.from(visited);
    }
    /**
     * Find all unique non-T pieces neighboring the given T piece group
     */
    findNeighboringPieces(tPieceIds) {
        const neighborSet = new Map();
        const neighborOffsets = [
            { dx: 0, dy: -1 }, // Top
            { dx: -1, dy: 0 }, // Left
            { dx: 1, dy: 0 }, // Right
            { dx: 0, dy: 1 }, // Bottom
        ];
        for (const tPieceId of tPieceIds) {
            const tPiece = this.gameState.pieces.find(p => p.id === tPieceId);
            if (!tPiece)
                continue;
            const cells = getPieceCells(tPiece);
            for (const cell of cells) {
                for (const offset of neighborOffsets) {
                    const neighborX = cell.x + offset.dx;
                    const neighborY = cell.y + offset.dy;
                    const neighbor = this.getPieceAtCell(neighborX, neighborY);
                    if (neighbor && neighbor.type !== 'T') {
                        neighborSet.set(neighbor.id, neighbor);
                    }
                }
            }
        }
        return Array.from(neighborSet.values());
    }
    /**
     * Find all T pieces neighboring the given cells
     */
    findNeighboringTPieces(cells) {
        const tPieceSet = new Map();
        const neighborOffsets = [
            { dx: 0, dy: -1 }, // Top
            { dx: -1, dy: 0 }, // Left
            { dx: 1, dy: 0 }, // Right
            { dx: 0, dy: 1 }, // Bottom
        ];
        for (const cell of cells) {
            for (const offset of neighborOffsets) {
                const neighborX = cell.x + offset.dx;
                const neighborY = cell.y + offset.dy;
                const neighbor = this.getPieceAtCell(neighborX, neighborY);
                if (neighbor && neighbor.type === 'T') {
                    tPieceSet.set(neighbor.id, neighbor);
                }
            }
        }
        return Array.from(tPieceSet.values());
    }
    /**
     * Recalculate all synchronization networks globally
     * Uses competitive multi-group membership where bridge pieces get the best cooldown
     */
    recalculateAllSynchronizationNetworks() {
        // Reset ALL pieces to active state and clear T-network membership and cooldown reductions
        for (const piece of this.gameState.pieces) {
            piece.state = 'active';
            piece.stateTimer = this.config.tileConfigs[piece.type].activeDuration;
            piece.tNetworkId = undefined;
            piece.cooldownDuration = undefined;
            if (piece.type === 'T') {
                piece.conductingFactor = undefined;
            }
        }
        // Find all T-piece connected components and assign network IDs
        const tPieceGroups = this.findAllTPieceGroups();
        let networkIdCounter = 0;
        for (const tGroup of tPieceGroups) {
            const networkId = `tnet-${networkIdCounter++}`;
            const neighbors = this.findNeighboringPieces(tGroup);
            // Assign T pieces to network and set their conducting factor from config
            for (const tPieceId of tGroup) {
                const tPiece = this.gameState.pieces.find(p => p.id === tPieceId);
                if (tPiece) {
                    tPiece.tNetworkId = networkId;
                    tPiece.conductingFactor = this.getPieceLeveledEffect(tPiece, this.config.tileConfigs['T'].conductingFactor ?? 0.5);
                }
            }
            // Assign direct neighbor pieces to network
            for (const neighbor of neighbors) {
                neighbor.tNetworkId = networkId;
            }
        }
        // Apply L piece cooldown reductions using pull-based approach
        for (const piece of this.gameState.pieces) {
            const config = this.config.tileConfigs[piece.type];
            if (config.cooldownDuration > 0) {
                const totalReduction = this.calculateReceivedCooldownReduction(piece);
                if (totalReduction > 0) {
                    piece.cooldownDuration = Math.max(0, config.cooldownDuration - totalReduction);
                }
            }
        }
    }
    /**
     * Find all T-piece connected components
     * Returns array of arrays, where each inner array contains T-piece IDs in one connected group
     */
    findAllTPieceGroups() {
        const allTPieces = this.gameState.pieces.filter(p => p.type === 'T');
        const visited = new Set();
        const groups = [];
        for (const tPiece of allTPieces) {
            if (visited.has(tPiece.id))
                continue;
            const group = this.findConnectedTPieces(tPiece.id);
            group.forEach(id => visited.add(id));
            groups.push(group);
        }
        return groups;
    }
    /**
     * Calculate the maximum currency based on storage bonuses from all pieces
     * Storage bonuses are enhanced by adjacent active enhancers
     */
    calculateMaxCurrency() {
        const totalStorageBonus = this.gameState.pieces.reduce((total, piece) => {
            const config = this.config.tileConfigs[piece.type];
            if (!config.storageBonus)
                return total;
            const enhancementMultiplier = this.calculateEnhancementMultiplier(piece);
            const leveledBonus = this.getPieceLeveledEffect(piece, config.storageBonus);
            const effectiveStorageBonus = leveledBonus * (1 + enhancementMultiplier);
            return total + effectiveStorageBonus;
        }, 0);
        return this.config.baseMaxCurrency + totalStorageBonus;
    }
    /**
     * Cap currency to current maximum
     */
    capCurrencyToMax() {
        if (this.gameState.currency > this.gameState.maxCurrency) {
            this.gameState.currency = this.gameState.maxCurrency;
        }
    }
}
//# sourceMappingURL=GameEngine.js.map